/** @format */

import {
	getAssetBlockKey,
	getQuestionContentBlocks,
	questionNumberDepth,
	hasDisplayableContent,
} from "@question-bank/shared";
import type { AnswerLinesConfig, PdfBlock, PdfContentTree, PdfPages, PdfQuestion, MarkSchemeImage } from "./types";
import type { PrefetchedImage } from "./prefetchImages";
import { labelledQuestions, type LabelledQuestion } from "../../utils/helpers";
import type { PaperGroup } from "../../hooks/usePaperQuestions";
import { isWelshLang, toLangCode, type ExportLanguage } from "../../utils/language";

/** Default page set, with every section on  */

const ALL_PAGES: PdfPages = {
	titlePage: true,
	questions: true,
	examinerComments: true,
	markingScheme: true,
};
/** Default answer-line settings: ruled lines shown, using the internal two-per-mark rule. */
export const DEFAULT_ANSWER_LINES: AnswerLinesConfig = {
	enabled: true,
};

/**
 * Flatten the carts groups into labelled questions, numbered exactly as the
 * assembled paper. Shared by the on-screen PDF view and the export trigger so both label identically.
 */
export const groupsToItems = (groups: PaperGroup[], language: ExportLanguage): LabelledQuestion[] =>
	groups.flatMap((group, groupIndex) => labelledQuestions(group.questions, group.entry, groupIndex, language));

/**
 * Adapter: labelled questions + title -> `PdfContentTree`. Question content
 * is resolved as text in `activeLang` (falling back to the other language).
 * Mark scheme images are still pre-fetched in the export trigger and passed in as data URIs
 * Input order is preserved as given.
 */

export const buildPdfContentTree = (
	items: LabelledQuestion[],
	title: string,
	activeLang: ExportLanguage,
	markingSchemeImageUris: Map<string, MarkSchemeImage | null> = new Map(),
	pages: PdfPages = ALL_PAGES,
	answerLines: AnswerLinesConfig = DEFAULT_ANSWER_LINES,
	contentImageUris: Map<string, PrefetchedImage | null> = new Map(),
): PdfContentTree => {
	const langCode = toLangCode(activeLang);

	const pdfQuestions: PdfQuestion[] = items.map(({ question, label, series }) => {
		const ownComments = (isWelshLang(activeLang) ? question.cy : question.en)?.examinersComments;
		const otherComments = (isWelshLang(activeLang) ? question.en : question.cy)?.examinersComments;
		const examinersComments = ownComments || otherComments || null;
		const isExaminerCommentsFallback = !ownComments && !!otherComments;
		// Derive indentation from the display label rather than the source
		// questionNumber. When the user cherry-picks parts, getPartLabel assigns
		// positional letters (e.g. "1(f)") that flatten the original hierarchy.
		// The visual indent must match the label the reader sees, not the source
		// ribbon depth.
		const depth = Math.max(0, questionNumberDepth(label) - 1);

		const blocks: PdfBlock[] = getQuestionContentBlocks(question)
			.map((block): PdfBlock | null => {
				if (block.type === "answerLines") {
					return block.lines > 0 ? { kind: "answerLines", lines: block.lines } : null;
				}
				if (block.type === "asset") {
					const key = getAssetBlockKey(block, langCode) ?? null;
					const image = key ? (contentImageUris.get(key) ?? null) : null;
					return {
						kind: "image",
						dataUri: image?.dataUri ?? null,
						width: image?.width ?? null,
						height: image?.height ?? null,
						widthPercent: block.width ?? null,
						isVector: image?.isVector ?? false,
						assetKey: key,
						alt: block.alt ?? null,
					};
				}
				const text = (langCode === "cy" ? (block.cy ?? block.en) : (block.en ?? block.cy))?.trim();
				return text ? { kind: "text", text } : null;
			})
			.filter((block): block is PdfBlock => block !== null);

		const markSchemeImage = markingSchemeImageUris.get(question.id) ?? null;
		return {
			id: question.id,
			groupId: question.parentQuestionId,
			displayLabel: label,
			depth,
			isParentHeader: !hasDisplayableContent(question),
			blocks,
			markSchemeImage: markSchemeImage?.dataUri ?? null,
			isMarkSchemeFallback: !!markSchemeImage?.dataUri && markSchemeImage.isFallBack,
			maxMarks: question.maxMarks ?? null,
			answerLines: question.answerLines ?? null,
			sourcePaperCode: question.paperCode,
			sourceSeries: series,
			examinersComments,
			isExaminerCommentsFallback,
		};
	});

	const entries = items
		.filter(({ question }) => (question.maxMarks ?? 0) > 0)
		.map(({ question, label }) => ({
			label,
			maxMarks: question.maxMarks ?? 0,
		}));

	const brand = items[0]?.question.brand ?? "wjec";

	return {
		brand,
		activeLang,
		title,
		pages,
		questions: pdfQuestions,
		marksTable: {
			entries,
		},
		answerLines,
	};
};
